import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Sparkles, Zap, Brain, Briefcase, TrendingUp,
  CheckCircle2, ShieldCheck, Languages, Trash2,
  ChevronRight, Radio, MapPin
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthPromptModal from '../components/AuthPromptModal';
import useSeo from '../hooks/useSeo';
import { ORG_JSONLD, WEBSITE_JSONLD } from '../lib/seoSchemas';

// Mobile app store URLs (single source of truth)
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.kms.knowmyschemes';
const APP_STORE_URL  = 'https://apps.apple.com/app/kms-smart/id6760982133';

// Live platform stats — update here when numbers grow
const PLATFORM_STATS = {
  schemes: 1000,
  jobs: 417,
  states: 3,
  beneficiaries: '50Cr+',
};

// Segments for "Who we help" bento. `sector` is the real DB sector name so
// the filter actually matches schemes. `title` is the friendly display name.
const SEGMENTS = [
  {
    key: 'farmers',
    title: 'Farmers',
    sector: 'Agriculture',
    tagline: 'PM-KISAN, crop insurance, drone subsidy & more',
    image: 'https://images.pexels.com/photos/32277759/pexels-photo-32277759.jpeg',
    accent: 'from-emerald-900/90 via-emerald-800/40',
    dot: 'bg-emerald-400',
  },
  {
    key: 'women',
    title: 'Women',
    sector: 'Women & Child',
    tagline: 'Ujjwala, Sukanya Samriddhi, Maha Mahila Nidhi',
    image: 'https://images.unsplash.com/photo-1589169011402-8b2cbd1ee593',
    accent: 'from-rose-900/90 via-rose-800/40',
    dot: 'bg-rose-400',
  },
  {
    key: 'students',
    title: 'Students',
    sector: 'Education',
    tagline: 'Scholarships, skill India, education loans',
    image: 'https://images.pexels.com/photos/18012463/pexels-photo-18012463.jpeg',
    accent: 'from-amber-900/90 via-amber-800/40',
    dot: 'bg-amber-400',
  },
  {
    key: 'families',
    title: 'Families',
    sector: 'Social Welfare',
    tagline: 'Ayushman Bharat, PMAY, ration & pensions',
    image: 'https://images.unsplash.com/photo-1683650666858-8d2e0b04a004',
    accent: 'from-teal-900/90 via-teal-800/40',
    dot: 'bg-teal-400',
  },
];

// Rotating punch taglines
const PUNCHLINES = [
  'Tell us who you are. We find what you deserve.',
  'Your eligibility, in under 60 seconds.',
  'Every rupee you qualify for — in one place.',
];

const HomeDesign1 = () => {
  useSeo({
    title: 'KnowMySchemes — 1000+ Government Schemes & 417+ Live Jobs | AI Eligibility in 60s',
    description: 'Discover every government welfare scheme and live recruitment notification you qualify for. KMS Smart AI matches your profile to 1000+ schemes and shows estimated benefits in INR. Free to start.',
    canonical: 'https://knowmyschemes.com/',
    jsonLd: [ORG_JSONLD, WEBSITE_JSONLD],
  });

  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { language } = useLanguage();

  const [trendingNews, setTrendingNews] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState('');
  const [punchIndex, setPunchIndex] = useState(0);
  const [openSegment, setOpenSegment] = useState(null);
  const segmentsRef = useRef(null);

  const handleProtectedNavigation = (path, message) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      setAuthModalMessage(message || 'Sign in to unlock personalised scheme matching.');
      setShowAuthModal(true);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        logout();
        navigate('/login');
      } else {
        alert('Failed to delete account. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
    }
    setShowDeleteConfirm(false);
  };

  // Rotate taglines
  useEffect(() => {
    const i = setInterval(() => setPunchIndex((p) => (p + 1) % PUNCHLINES.length), 4200);
    return () => clearInterval(i);
  }, []);

  // News ticker
  const fetchTrendingNews = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/news/trending?lang=${language}`);
      if (response.ok) {
        const data = await response.json();
        if (data.news && data.news.length > 0) setTrendingNews(data.news);
      }
    } catch (e) {
      console.error('Error fetching trending news:', e);
    }
  }, [language]);

  useEffect(() => {
    fetchTrendingNews();
    const interval = setInterval(fetchTrendingNews, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchTrendingNews]);

  // Close segment on outside click
  useEffect(() => {
    const handler = (e) => {
      if (openSegment && segmentsRef.current && !segmentsRef.current.contains(e.target)) {
        setOpenSegment(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [openSegment]);

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-emerald-200">
      <Header />

      {/* ========================= HERO ========================= */}
      <section
        className="relative overflow-hidden border-b border-slate-200"
        data-testid="home-hero"
      >
        {/* Soft grid backdrop */}
        <div
          className="absolute inset-0 -z-10 opacity-[0.35]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.06) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage:
              'radial-gradient(ellipse at top, black 40%, transparent 80%)',
          }}
        />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-2 pb-16 sm:pt-14 sm:pb-24 lg:pt-20">
          {/* Eyebrow */}
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-3 sm:mb-6 animate-in fade-in slide-in-from-top-2 duration-500">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
            </span>
            <span className="uppercase tracking-[0.18em]">India&apos;s largest welfare discovery engine</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
            {/* Left: Hero copy (mobile order 2 so KMS Smart magnet leads above the fold) */}
            <div className="order-2 lg:order-1 lg:col-span-7 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <h1 className="font-black tracking-tighter text-slate-900 text-[2.25rem] leading-[0.98] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] xl:leading-[0.92]">
                Find every benefit<br />
                <span className="inline-flex items-baseline flex-wrap gap-x-3">
                  <span>the</span>
                  <span className="relative">
                    <span className="relative z-10 bg-gradient-to-br from-emerald-600 via-emerald-500 to-green-500 bg-clip-text text-transparent">
                      Government
                    </span>
                    <span
                      aria-hidden
                      className="absolute inset-x-0 -bottom-1 h-[0.24em] rounded-full bg-emerald-200/80"
                    />
                  </span>
                </span>
                <br />
                owes you.
              </h1>

              {/* Rotating punchline */}
              <div
                key={punchIndex}
                className="mt-7 text-lg sm:text-xl max-w-xl text-slate-600 leading-relaxed animate-in fade-in slide-in-from-bottom-2 duration-500"
                data-testid="hero-punchline"
              >
                <span className="font-semibold text-slate-900">&ldquo;{PUNCHLINES[punchIndex].split('.')[0]}.&rdquo;</span>{' '}
                {PUNCHLINES[punchIndex].split('.').slice(1).join('.').trim()}
              </div>

              {/* CTAs */}
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => navigate('/impact-analyser')}
                  className="group h-14 px-7 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full text-base font-semibold transition-all shadow-[0_8px_32px_rgba(6,95,70,0.28)] hover:shadow-[0_10px_40px_rgba(4,120,87,0.4)] active:scale-[0.98]"
                  data-testid="hero-kms-smart-cta"
                >
                  <Sparkles className="mr-2 h-4 w-4 text-amber-300" />
                  Try KMS Smart — free
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  onClick={() => navigate('/schemes')}
                  variant="outline"
                  className="h-14 px-7 border-slate-300 hover:bg-slate-50 text-slate-900 rounded-full text-base font-medium active:scale-[0.98]"
                  data-testid="hero-browse-schemes-cta"
                >
                  Browse {PLATFORM_STATS.schemes}+ schemes
                </Button>
              </div>

              {/* Trust row */}
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><Languages className="h-4 w-4 text-emerald-600" /> English, Hindi & 3 regional</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-amber-600" /> Trusted by lakhs of citizens</span>
              </div>
            </div>

            {/* Right: KMS Smart Magnet (mobile order 1 — above the fold) */}
            <div className="order-1 lg:order-2 lg:col-span-5 relative">
              <div
                className="relative rounded-3xl bg-[#052E1F] text-white p-7 sm:p-8 overflow-hidden shadow-[0_30px_80px_-20px_rgba(5,46,31,0.55)] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-200"
                data-testid="kms-smart-magnet"
              >
                {/* Ambient glow */}
                <div className="pointer-events-none absolute -top-28 -right-28 h-72 w-72 rounded-full bg-emerald-400/25 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-green-400/20 blur-3xl" />
                {/* Tracing beam border */}
                <div className="pointer-events-none absolute inset-0 rounded-3xl">
                  <div className="absolute inset-[-1px] rounded-3xl border border-white/10" />
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-3xl"
                    style={{
                      background:
                        'conic-gradient(from var(--angle, 0deg) at 50% 50%, transparent 70%, rgba(255,255,255,0.35) 78%, transparent 86%)',
                      animation: 'kms-spin 6s linear infinite',
                      WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                      WebkitMaskComposite: 'xor',
                      maskComposite: 'exclude',
                      padding: 1,
                    }}
                  />
                </div>

                {/* Header */}
                <div className="relative flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15 backdrop-blur-sm">
                      <Brain className="h-5 w-5 text-emerald-200" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-200/80">KMS Smart</div>
                      <div className="text-sm font-semibold">AI Impact Analyser</div>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-medium text-emerald-300 ring-1 ring-emerald-400/20">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live
                  </span>
                </div>

                {/* Demo flow */}
                <div className="relative space-y-3 mb-7">
                  {[
                    { step: '01', title: 'Tell us who you are', sub: 'Occupation, state, age, income — 5 short questions' },
                    { step: '02', title: 'AI matches your profile', sub: 'Our AI runs 1000+ schemes against your eligibility' },
                    { step: '03', title: 'See what you deserve', sub: 'Estimated benefits in INR + action steps + deadlines' },
                  ].map((s) => (
                    <div key={s.step} className="group flex items-start gap-4 rounded-xl bg-white/[0.04] p-3.5 ring-1 ring-white/[0.06] hover:bg-white/[0.08] transition">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 text-[11px] font-bold tracking-wider">
                        {s.step}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white">{s.title}</div>
                        <div className="text-xs text-emerald-200/70 mt-0.5">{s.sub}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-emerald-200/40 group-hover:text-white transition-colors" />
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button
                  onClick={() => navigate('/impact-analyser')}
                  className="relative group w-full h-14 rounded-2xl bg-white text-slate-900 hover:bg-slate-100 font-semibold text-base active:scale-[0.98]"
                  data-testid="kms-smart-magnet-cta"
                >
                  <Zap className="mr-2 h-4 w-4 text-amber-500" />
                  Find my schemes in 60 seconds
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>

                <div className="relative mt-3 text-center text-[11px] text-emerald-200/60">
                  Powered by our AI · Average time to answer: 48s
                </div>
              </div>

              {/* Floating stat pill */}
              <div
                className="hidden sm:flex absolute -left-4 -bottom-4 items-center gap-2 rounded-full bg-white px-4 py-2.5 shadow-xl ring-1 ring-slate-200"
                aria-hidden
              >
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-semibold text-slate-700">+{PLATFORM_STATS.jobs} new jobs this month</span>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @property --angle {
            syntax: '<angle>';
            initial-value: 0deg;
            inherits: false;
          }
          @keyframes kms-spin {
            to { --angle: 360deg; }
          }
        `}</style>
      </section>

      {/* ========================= LIVE NEWS TICKER ========================= */}
      <section className="border-b border-slate-200 bg-slate-50" data-testid="home-news-ticker">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Radio className="h-4 w-4 text-red-600" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-red-600">Live</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="whitespace-nowrap inline-block text-sm text-slate-700 animate-ticker">
              {(trendingNews.length > 0 ? trendingNews : [
                { emoji: '📢', source: 'Loading', title: 'Fetching latest government news…' },
              ])
                .concat(trendingNews.length > 0 ? trendingNews : [])
                .map((n, i) => (
                  <span key={i} className="mx-8">
                    <span className="mr-2">{n.emoji || '📰'}</span>
                    <strong className="text-slate-900">{n.source}:</strong> {n.title}
                  </span>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================= STATS BAR ========================= */}
      <section className="border-b border-slate-200" data-testid="home-stats-bar">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
            {[
              { v: `${PLATFORM_STATS.schemes}+`, label: 'Government schemes', sub: 'Central + state', accent: 'text-emerald-600' },
              { v: `${PLATFORM_STATS.jobs}+`, label: 'Live job notifications', sub: 'UPSC, SSC, Railway, Banking…', accent: 'text-amber-600' },
              { v: PLATFORM_STATS.states, label: 'States live today', sub: 'AP · TG · KA (+ Central)', accent: 'text-green-600' },
              { v: PLATFORM_STATS.beneficiaries, label: 'Beneficiaries impacted', sub: 'Across 10 years of data', accent: 'text-teal-600' },
            ].map((s, i) => (
              <div
                key={i}
                className={`py-8 px-4 sm:py-10 sm:px-6 ${i === 0 ? 'lg:pl-0' : ''} ${i === 3 ? 'lg:pr-0' : ''} ${i % 2 === 0 ? 'border-slate-200' : ''}`}
                data-testid={`stat-${i}`}
              >
                <div className={`text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter ${s.accent}`}>
                  {s.v}
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= JOBS FEATURE BAND ========================= */}
      <section className="py-20 lg:py-28" data-testid="home-jobs-band">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 ring-1 ring-amber-200 mb-5">
              <Briefcase className="h-3.5 w-3.5 text-amber-700" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-800">New in 2026</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.02]">
              Every live government<br />recruitment, in one feed.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              {PLATFORM_STATS.jobs}+ active notifications across UPSC, SSC, Railway, Banking, PSU, Defence, Teaching and State PSCs — updated every 24 hours by our scrapers.
            </p>
            <Button
              onClick={() => navigate('/schemes?tab=jobs')}
              className="mt-8 h-12 px-6 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-full font-semibold active:scale-[0.98]"
              data-testid="jobs-feature-cta"
            >
              Explore {PLATFORM_STATS.jobs}+ live jobs
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="jobs-category-grid">
              {[
                // `q` is the URL query param passed to the jobs listing. We prefer
                // `category=` when the backend has an exact category match,
                // otherwise `search=` (free-text on title/org/description).
                { t: 'UPSC',      c: 'Civil services',            q: 'search=UPSC' },
                { t: 'SSC',       c: 'CGL / CHSL / MTS',          q: 'search=SSC' },
                { t: 'Railway',   c: 'RRB / RPF',                 q: 'category=Railway' },
                { t: 'Banking',   c: 'IBPS / SBI / RBI',          q: 'category=Banking+and+Finance' },
                { t: 'Defence',   c: 'Army / Navy / Air Force',   q: 'category=Defence+and+Paramilitary' },
                { t: 'PSU',       c: 'ONGC / BHEL / NTPC',        q: 'category=Engineering+and+Technical' },
                { t: 'State PSC', c: 'APPSC / TSPSC / KPSC',      q: 'search=PSC' },
                { t: 'Teaching',  c: 'CTET / KVS / NVS',          q: 'category=Teaching+and+Education' },
                { t: 'Tech / IT', c: 'NIC / CDAC / ISRO',         q: 'category=Technology+and+IT' },
              ].map((j, i) => (
                <div
                  key={j.t}
                  className="group relative rounded-2xl border border-slate-200 bg-white p-5 hover:border-amber-400 hover:-translate-y-0.5 hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/schemes?tab=jobs&${j.q}`)}
                  data-testid={`jobs-cat-${j.t.toLowerCase().replace(/\s+/g, '-').replace(/\//g,'-')}`}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="text-lg font-bold text-slate-900 group-hover:text-amber-700 transition-colors">{j.t}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{j.c}</div>
                  <ArrowRight className="absolute top-4 right-4 h-4 w-4 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================= WHO WE HELP — BENTO ========================= */}
      <section className="py-20 lg:py-28 bg-slate-50 border-y border-slate-200" data-testid="home-who-we-help">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 ring-1 ring-slate-200 mb-4">
              <MapPin className="h-3.5 w-3.5 text-slate-600" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-600">Built for real Indians</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.02]">
              Whoever you are,<br />there&apos;s a scheme for you.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Every segment below has dozens of schemes quietly waiting to be claimed. Tap a card to see what&apos;s popular.
            </p>
          </div>

          <div
            ref={segmentsRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-5"
          >
            {SEGMENTS.map((seg, idx) => {
              const span = idx === 0 ? 'lg:col-span-7 lg:row-span-2' : idx === 1 ? 'lg:col-span-5' : idx === 2 ? 'lg:col-span-5' : 'lg:col-span-7';
              const aspect = idx === 0 ? 'min-h-[340px] lg:min-h-[520px]' : 'min-h-[220px] lg:min-h-[250px]';
              const isOpen = openSegment === seg.key;
              return (
                <button
                  key={seg.key}
                  onClick={() => setOpenSegment(isOpen ? null : seg.key)}
                  className={`group relative overflow-hidden rounded-3xl text-left ${span} ${aspect} ring-1 ring-slate-200 hover:ring-slate-300 hover:-translate-y-0.5 transition-all duration-300`}
                  data-testid={`segment-${seg.key}`}
                >
                  <img
                    src={seg.image}
                    alt={seg.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${seg.accent} to-transparent`} />

                  <div className="relative h-full flex flex-col justify-end p-6 sm:p-7">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`h-2 w-2 rounded-full ${seg.dot} shadow-[0_0_12px] shadow-current`} />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">Segment</span>
                    </div>
                    <h3 className="text-3xl sm:text-4xl font-black tracking-tight text-white">{seg.title}</h3>
                    <p className="mt-2 text-sm sm:text-base text-white/85 max-w-md">{seg.tagline}</p>
                    <div
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:gap-3 transition-all"
                      onClick={(e) => { e.stopPropagation(); navigate(`/schemes?sector=${encodeURIComponent(seg.sector)}`); }}
                      role="button"
                      tabIndex={0}
                    >
                      Explore {seg.title.toLowerCase()} schemes
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================= MOBILE APP BAND ========================= */}
      <section className="py-20 lg:py-28" data-testid="home-app-band">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-[#052E1F] text-white p-10 sm:p-14 lg:p-20">
            <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-emerald-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 ring-1 ring-white/15 mb-5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.15em]">Now live on iOS & Android</span>
                </div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
                  KMS Smart in your pocket.<br />
                  <span className="bg-gradient-to-r from-emerald-300 via-green-300 to-teal-300 bg-clip-text text-transparent">
                    Eligibility checks on the go.
                  </span>
                </h2>
                <p className="mt-5 text-lg text-white/70 max-w-xl">
                  Native push alerts for every new scheme. Offline scheme browsing. Bilingual UX. Free forever on web &amp; mobile.
                </p>

                <div className="mt-8 flex flex-wrap gap-3 items-center">
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block active:scale-[0.98] transition-transform hover:opacity-90"
                    aria-label="Download on the App Store"
                    data-testid="app-store-link"
                  >
                    <img
                      src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                      alt="Download on the App Store"
                      className="h-14"
                      loading="lazy"
                    />
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block active:scale-[0.98] transition-transform hover:opacity-90"
                    aria-label="Get it on Google Play"
                    data-testid="play-store-link"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-[72px] -my-2"
                      loading="lazy"
                    />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5 relative">
                <div className="aspect-[4/5] max-w-sm mx-auto relative">
                  <div className="absolute inset-0 rounded-[2.5rem] bg-white/5 ring-1 ring-white/10 backdrop-blur-sm shadow-2xl overflow-hidden">
                    {/* Faux app screen */}
                    <div className="h-full w-full p-6 flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                          <Brain className="h-4 w-4 text-white" />
                        </div>
                        <div className="text-[10px] font-medium text-white/60">KMS Smart</div>
                      </div>
                      <div className="text-xl font-bold text-white leading-tight mb-1">Your top 3 matches</div>
                      <div className="text-[10px] text-white/50 mb-5">Based on your profile · 2 min ago</div>
                      {[
                        { n: 'PM-KISAN', v: '₹6,000/yr', ok: true },
                        { n: 'AP Drone Subsidy', v: '₹8,00,000', ok: true },
                        { n: 'Fasal Bima', v: 'Insurance', ok: true },
                      ].map((m) => (
                        <div key={m.n} className="mb-3 rounded-xl bg-white/[0.06] ring-1 ring-white/10 p-3.5 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-white">{m.n}</div>
                            <div className="text-[11px] text-emerald-300">{m.v}</div>
                          </div>
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        </div>
                      ))}
                      <div className="mt-auto rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 p-3.5 text-center text-sm font-semibold">
                        View full report
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================= FINAL CTA ========================= */}
      <section className="pb-24 sm:pb-32" data-testid="home-final-cta">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.02]">
            Don&apos;t leave money<br />on the table.
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto">
            Every minute, ₹1.2 crore in benefits is transferred to someone just like you. Find what you qualify for in under a minute.
          </p>
          <Button
            onClick={() => navigate('/impact-analyser')}
            className="mt-10 h-14 px-8 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-semibold text-base shadow-[0_8px_32px_rgba(6,95,70,0.28)] active:scale-[0.98]"
            data-testid="final-kms-smart-cta"
          >
            <Sparkles className="mr-2 h-4 w-4 text-amber-300" />
            Run KMS Smart — free
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Ticker animation */}
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker 60s linear infinite;
        }
        .animate-ticker:hover { animation-play-state: paused; }
      `}</style>

      {/* Delete Account Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete Account?</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your account? All your saved schemes and preferences will be permanently removed.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => setShowDeleteConfirm(false)} variant="outline" className="flex-1 border-gray-300">
                Cancel
              </Button>
              <Button onClick={handleDeleteAccount} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}

      <AuthPromptModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} message={authModalMessage} />
      <Footer />
    </div>
  );
};

export default HomeDesign1;

